import React, { useState, useEffect } from 'react';
import { workflowsAPI } from '../api/client';
import { extractErrorMessage } from '../utils/errors';
import WorkflowTemplateSelect from './WorkflowTemplateSelect';

/**
 * Picks a workflow and submits for approval. Reused for both a document's
 * first-cycle submission and a revision's -- the two approve different rows
 * under different endpoints, so the caller supplies `onSave` and whatever
 * "already has an open workflow" state applies; this component only ever
 * picks a template and confirms.
 *
 * Props are generic (`hierarchyNodeId`/`documentId`/`isResuming`/
 * `pendingTemplateName`) rather than a whole `document` object, precisely so
 * a DocumentRevision can drive this with the same component.
 */
function SubmitForApprovalModal({
  title = 'Submit for Approval',
  hierarchyNodeId, documentId, isResuming, pendingTemplateName,
  onSave, onClose,
}) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loading, setLoading] = useState(!isResuming);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isResuming) return;
    workflowsAPI.getApplicableTemplates(hierarchyNodeId, documentId)
      .then(res => {
        const list = res.data;
        setTemplates(list);
        // Auto-select: exactly one applicable template, or -- failing that --
        // the one this document used last time. Both are hints; nothing here
        // restricts the choice, and either can be overridden before confirming.
        if (list.length === 1) {
          setSelectedTemplateId(String(list[0].id));
        } else {
          const lastUsed = list.find(t => t.is_last_used);
          if (lastUsed) setSelectedTemplateId(String(lastUsed.id));
        }
      })
      .catch(err => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = async () => {
    if (!isResuming && !selectedTemplateId) {
      alert('Select a workflow template');
      return;
    }
    try {
      setSubmitting(true);
      await onSave(isResuming ? null : parseInt(selectedTemplateId));
    } catch (err) {
      setError(extractErrorMessage(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>{isResuming ? 'Resume Submission' : title}</h3>
        </div>

        <div style={{ padding: '15px' }}>
          {error && <div className="alert alert-error">{error}</div>}

          {isResuming ? (
            <p>
              This was sent back for revision. Resubmitting will resume workflow{' '}
              <strong>{pendingTemplateName}</strong> from where it left off.
            </p>
          ) : loading ? (
            <div className="loading">Loading applicable workflows...</div>
          ) : templates.length === 0 ? (
            <p>
              No workflows are defined for this document's location. Define one at this
              location or above it on the Hierarchy page.
            </p>
          ) : (
            <div className="form-group">
              <label>Workflow *</label>
              <WorkflowTemplateSelect
                templates={templates}
                value={selectedTemplateId}
                onChange={(id) => setSelectedTemplateId(String(id))}
                disabled={submitting}
              />
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={handleConfirm}
            disabled={submitting || (!isResuming && (loading || templates.length === 0))}
          >
            {submitting ? '⏳ Submitting...' : isResuming ? 'Resume Submission' : 'Submit for Approval'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SubmitForApprovalModal;
